import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import Alert from "react-bootstrap/esm/Alert";
import Button from "react-bootstrap/esm/Button";

interface ServiceInfo {
    date: string;
    serviceLabel: string;
}

interface SwapApproval {
    id: string;
    fromName: string;
    toName: string;
    service: ServiceInfo | null;
    counter: ServiceInfo | null;
    message: string;
}

interface SwapApprovalsProps {
    gemeindeId: string;
    /** Called after a swap was confirmed, so the list can reload the changed services. */
    onChanged: () => void;
    onError: (message: string) => void;
}

/**
 * Swaps that two preachers agreed on and that wait for the admin's confirmation
 * (Gemeinde setting swapRequiresAdminApproval). Renders nothing if there are none.
 */
export default function SwapApprovals({ gemeindeId, onChanged, onError }: SwapApprovalsProps) {
    const { t } = useTranslation();
    const [approvals, setApprovals] = useState<SwapApproval[]>([]);
    const [busy, setBusy] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const list = httpsCallable<{ gemeindeId: string }, { approvals: SwapApproval[] }>(functions, "listSwapApprovals");
            setApprovals((await list({ gemeindeId })).data.approvals);
        } catch {
            // Not being able to load approvals must not break the service list.
            setApprovals([]);
        }
    }, [gemeindeId]);

    useEffect(() => {
        load();
    }, [load]);

    const decide = async (id: string, approve: boolean) => {
        setBusy(id);
        try {
            await httpsCallable(functions, "decideSwap")({ gemeindeId, requestId: id, approve });
            await load();
            onChanged();
        } catch (err: any) {
            onError(err?.message || (t("errors.anErrorOccurred") as string));
        } finally {
            setBusy(null);
        }
    };

    const describe = (info: ServiceInfo | null) =>
        info
            ? `${info.serviceLabel}, ${new Date(info.date).toLocaleDateString(t("calendar.locale") as string, {
                  weekday: "short",
                  day: "numeric",
                  month: "long",
                  timeZone: "UTC",
              })}`
            : "";

    if (approvals.length === 0) return null;

    return (
        <div className="mb-4">
            {approvals.map((approval) => (
                <Alert variant="warning" key={approval.id} className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
                    <div>
                        <div className="fw-semibold">{t("swap.approvalTitle")}</div>
                        <div>
                            {describe(approval.service)}: {approval.fromName} → {approval.toName}
                        </div>
                        {approval.counter && (
                            <div>
                                {describe(approval.counter)}: {approval.toName} → {approval.fromName}
                            </div>
                        )}
                        {approval.message && <div className="small text-muted mt-1">„{approval.message}“</div>}
                    </div>
                    <div className="d-flex gap-2">
                        <Button size="sm" variant="primary" disabled={busy === approval.id} onClick={() => decide(approval.id, true)}>
                            {t("swap.approve")}
                        </Button>
                        <Button size="sm" variant="outline-secondary" disabled={busy === approval.id} onClick={() => decide(approval.id, false)}>
                            {t("swap.reject")}
                        </Button>
                    </div>
                </Alert>
            ))}
        </div>
    );
}
